// Helpers for interacting with the externally-loaded Siena live chat widget.
//
// The widget bundle (siena-chat-web.js) exposes a documented global API on
// `window` once its embedded iframe has booted. The docs-site just needs to
// call that API — dispatching a custom event does nothing because the widget
// bundle doesn't subscribe to events. See:
// siena-web/packages/live-chat/WIDGET_LAUNCH_API.md

type SienaLauncher = (message?: string, email?: string) => void;

interface SienaWindow extends Window {
  SienaLaunchChat?: SienaLauncher;
  SienaLaunchWidget?: SienaLauncher;
  SienaLaunchFullscreen?: SienaLauncher;
  SienaLiveChatWidget?: {
    launch?: SienaLauncher;
    launchFullscreen?: SienaLauncher;
    isReady?: () => boolean;
  };
}

const WIDGET_READY_MAX_ATTEMPTS = 30; // ~3s total with 100ms interval
const WIDGET_READY_INTERVAL_MS = 100;

/**
 * Resolve the best-available launcher exposed by the widget bundle.
 * Order of preference:
 *   1. `SienaLiveChatWidget.launch` (official advanced API)
 *   2. `SienaLaunchChat`            (simple documented global)
 *   3. `SienaLaunchWidget`          (alias)
 */
const pickLauncher = (): SienaLauncher | null => {
  if (typeof window === "undefined") return null;
  const w = window as SienaWindow;
  if (w.SienaLiveChatWidget?.launch) {
    return w.SienaLiveChatWidget.launch.bind(w.SienaLiveChatWidget);
  }
  if (typeof w.SienaLaunchChat === "function") return w.SienaLaunchChat;
  if (typeof w.SienaLaunchWidget === "function") return w.SienaLaunchWidget;
  return null;
};

const pickFullscreenLauncher = (): SienaLauncher | null => {
  if (typeof window === "undefined") return null;
  const w = window as SienaWindow;
  if (w.SienaLiveChatWidget?.launchFullscreen) {
    return w.SienaLiveChatWidget.launchFullscreen.bind(w.SienaLiveChatWidget);
  }
  if (typeof w.SienaLaunchFullscreen === "function") {
    return w.SienaLaunchFullscreen;
  }
  return pickLauncher();
};

/**
 * Open the live chat widget. Retries briefly because the widget script is
 * async and may not yet have attached its globals when the user clicks.
 *
 * Returns a promise that resolves once the launcher has been invoked, or
 * rejects if the widget never becomes ready.
 */
export const openLiveChat = (options?: {
  message?: string;
  email?: string;
  fullscreen?: boolean;
}): Promise<void> => {
  const { message, email, fullscreen = false } = options ?? {};

  return new Promise((resolve, reject) => {
    let attempts = 0;

    const tick = () => {
      const launcher = fullscreen ? pickFullscreenLauncher() : pickLauncher();
      if (launcher) {
        try {
          launcher(message, email);
          resolve();
        } catch (err) {
          reject(err);
        }
        return;
      }

      attempts += 1;
      if (attempts >= WIDGET_READY_MAX_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.warn(
          "[liveChat] widget never exposed launcher globals (SienaLaunchChat / SienaLiveChatWidget.launch) — is the script loaded?"
        );
        reject(new Error("Siena live chat widget not ready"));
        return;
      }

      window.setTimeout(tick, WIDGET_READY_INTERVAL_MS);
    };

    tick();
  });
};
